/* Behavior for the public directory + guide surfaces (/resources,
 * /family-newsletters, /family-newsletters-{city}).
 *
 * Both features are progressive enhancements: without JS the filter input is
 * hidden (it is rendered inside a [hidden] wrapper that this script reveals)
 * and the section nav stays a plain list of anchor links.
 */

/* ── Live filter over a long grouped link list ─────────────────────────────
 * Markup contract:
 *   [data-fhq-filter]                  the <input>
 *   [data-fhq-filter-shell]            wrapper revealed once JS runs
 *   [data-fhq-filter-group]            a group (state block) that hides when empty
 *   [data-fhq-filter-item]             an item; filtered on data-fhq-filter-text
 *   [data-fhq-filter-count]            live region for the result count
 *   [data-fhq-filter-empty]            shown when nothing matches
 */
function initFilter() {
  const input = document.querySelector("[data-fhq-filter]");
  if (!input) return;

  const shell = document.querySelector("[data-fhq-filter-shell]");
  if (shell) shell.hidden = false;

  const countEl = document.querySelector("[data-fhq-filter-count]");
  const emptyEl = document.querySelector("[data-fhq-filter-empty]");
  const groups = Array.from(document.querySelectorAll("[data-fhq-filter-group]")).map((group) => ({
    el: group,
    items: Array.from(group.querySelectorAll("[data-fhq-filter-item]")).map((item) => ({
      el: item,
      // Precomputed server-side (city + state + abbreviation) so a search for
      // "richmond va" matches without re-reading the DOM on every keystroke.
      text: (item.getAttribute("data-fhq-filter-text") || item.textContent || "").toLowerCase(),
    })),
  }));

  const total = groups.reduce((sum, group) => sum + group.items.length, 0);
  const countTemplate = countEl ? countEl.getAttribute("data-fhq-filter-count") || "" : "";

  const render = () => {
    const query = input.value.trim().toLowerCase();
    // Every whitespace-separated token must appear somewhere in the item's text,
    // rather than the whole query matching as one substring. The item text is
    // "richmond virginia va", so a plain substring test fails the most natural
    // query of all — "richmond va" — while this matches it in any word order.
    const terms = query === "" ? [] : query.split(/\s+/);
    let matches = 0;

    groups.forEach((group) => {
      let groupMatches = 0;
      group.items.forEach((item) => {
        const hit = terms.every((term) => item.text.indexOf(term) !== -1);
        item.el.classList.toggle("fhq-hidden", !hit);
        if (hit) groupMatches += 1;
      });
      group.el.classList.toggle("fhq-hidden", groupMatches === 0);
      matches += groupMatches;
    });

    if (countEl) {
      countEl.textContent =
        query === ""
          ? countTemplate.replace("{count}", String(total))
          : `${matches} of ${total} match “${input.value.trim()}”`;
    }
    if (emptyEl) emptyEl.classList.toggle("fhq-hidden", matches !== 0);
  };

  let pending = 0;
  input.addEventListener("input", () => {
    // Timeout throttle rather than requestAnimationFrame: some embedded webviews
    // suspend rAF entirely, which would leave the filter permanently stale (the
    // same reason the help-article scrollspy avoids it). One render per burst of
    // typing keeps 600+ nodes from triggering a layout pass per keystroke.
    if (pending) window.clearTimeout(pending);
    pending = window.setTimeout(() => {
      pending = 0;
      render();
    }, 60);
  });

  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && input.value !== "") {
      input.value = "";
      render();
    }
  });

  render();
}

/* ── Sticky section nav: highlight the section currently in view ─────────── */
function initSectionNav() {
  const nav = document.querySelector("[data-fhq-subnav]");
  if (!nav) return;

  const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
  if (links.length === 0) return;

  // Keep the active pill visible within the bar's own horizontal scroller. Deliberately
  // not scrollIntoView({block:"nearest"}): that walks every scrollable ancestor, and
  // .fhq-subnav's position:sticky doesn't actually stick to the viewport here (its
  // nearest CSS scroll-container ancestor is body, which never scrolls — html does) —
  // so "nearest" reads the bar's current in-flow position and yanks the whole page
  // toward it on every section change. Scrolling only this element's scrollLeft can't
  // ever move the page, regardless of that.
  const inner = nav.querySelector(".fhq-subnav-inner") || nav;
  function scrollPillIntoView(link) {
    const containerRect = inner.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    if (linkRect.left < containerRect.left) {
      inner.scrollLeft -= containerRect.left - linkRect.left;
    } else if (linkRect.right > containerRect.right) {
      inner.scrollLeft += linkRect.right - containerRect.right;
    }
  }

  // Scroll + timeout rather than IntersectionObserver, matching the help-article
  // scrollspy: embedded webviews that throttle or disable IO callbacks would
  // otherwise freeze the highlight after the first scroll.
  const pairs = [];
  links.forEach((link) => {
    const id = decodeURIComponent(link.getAttribute("href").slice(1));
    const section = id ? document.getElementById(id) : null;
    if (section) pairs.push({ link, section });
  });
  if (pairs.length === 0) return;

  let activeLink = null;
  let scheduled = 0;
  const update = () => {
    scheduled = 0;
    // The section whose top has most recently passed the reading line (below
    // the site header and this nav) is the one being read.
    const readingLine = Math.max(160, window.innerHeight * 0.28);
    let current = pairs[0];
    pairs.forEach((pair) => {
      if (pair.section.getBoundingClientRect().top <= readingLine) current = pair;
    });

    if (current.link === activeLink) return;
    // The first sync runs on load, before the visitor has scrolled at all. On short
    // viewports the subnav's natural (pre-sticky) position can sit below the fold,
    // so calling scrollIntoView here would yank the page down on its own. Only
    // scroll the pill into view once this is reacting to real scroll/resize input.
    const isInitialSync = activeLink === null;
    if (activeLink) activeLink.removeAttribute("aria-current");
    activeLink = current.link;
    activeLink.setAttribute("aria-current", "true");
    if (!isInitialSync) {
      scrollPillIntoView(activeLink);
    }
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = window.setTimeout(update, 80);
  };

  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule, { passive: true });
  update();
}

function init() {
  initFilter();
  initSectionNav();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
